import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { FULL_CURRENCIES } from '../data/currency-data';

export interface CurrencyFormattingConfig {
  code: string;          // e.g. 'USD'
  symbol: string;        // e.g. '$'
  symbolPosition: 'before' | 'after';
  thousandSeparator: string; // e.g. ',' or ' '
  decimalSeparator: string;  // e.g. '.' or ','
  precision: number;     // decimals
  // Optional display name
  label?: string;
}

// Base fallback defaults for any currency attributes not specified in FULL_CURRENCIES.
const BASE_DEFAULT: Omit<CurrencyFormattingConfig, 'code' | 'symbol'> = {
  symbolPosition: 'before',
  thousandSeparator: ',',
  decimalSeparator: '.',
  precision: 2,
};

// Legacy minimal overrides (ensures specific positional / separator differences retained where needed).
const LEGACY_OVERRIDES: Record<string, Partial<CurrencyFormattingConfig>> = {
  EUR: { symbolPosition: 'after', thousandSeparator: ' ', decimalSeparator: ',', precision: 2 },
  MAD: { symbolPosition: 'after', thousandSeparator: ' ', decimalSeparator: ',', precision: 2, symbol: 'DH' },
  JPY: { precision: 0 },
};

function buildCurrencyMap(): Record<string, CurrencyFormattingConfig> {
  const map: Record<string, CurrencyFormattingConfig> = {};
  for (const c of FULL_CURRENCIES) {
    const override = LEGACY_OVERRIDES[c.code] || {};
    map[c.code] = {
      code: c.code,
      symbol: c.symbol,
      label: c.label,
      symbolPosition: (c as any).symbolPosition ?? override.symbolPosition ?? BASE_DEFAULT.symbolPosition,
      thousandSeparator: (c as any).thousandSeparator ?? override.thousandSeparator ?? BASE_DEFAULT.thousandSeparator,
      decimalSeparator: (c as any).decimalSeparator ?? override.decimalSeparator ?? BASE_DEFAULT.decimalSeparator,
      precision: (c as any).precision ?? override.precision ?? BASE_DEFAULT.precision,
    };
  }
  return map;
}

const STORAGE_KEY = 'app.currency.code';

@Injectable({ providedIn: 'root' })
export class CurrencyService {
  private currencyMap: Record<string, CurrencyFormattingConfig> = buildCurrencyMap();
  private currency$ = new BehaviorSubject<CurrencyFormattingConfig>(this.getInitialCurrency());

  private getInitialCurrency(): CurrencyFormattingConfig {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    if (stored && this.currencyMap[stored]) {
      return this.currencyMap[stored];
    }
    return this.currencyMap['EUR'];
  }

  get currencyChanges(): Observable<CurrencyFormattingConfig> {
    return this.currency$.asObservable();
  }

  get current(): CurrencyFormattingConfig {
    return this.currency$.value;
  }

  setCurrency(code: string): void {
    const cfg = this.currencyMap[code];
    if (!cfg) return;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, code);
    }
    this.currency$.next(cfg);
  }

  registerCustom(config: CurrencyFormattingConfig) {
    this.currencyMap[config.code] = config;
  }

  getConfig(code: string): CurrencyFormattingConfig | null {
    return this.currencyMap[code] || null;
  }

  getAllCurrencies(): CurrencyFormattingConfig[] {
    // Return sorted by label for UI friendliness
    return Object.values(this.currencyMap).sort((a, b) => (a.label || a.code).localeCompare(b.label || b.code));
  }

  formatRaw(value: number | string, override?: Partial<CurrencyFormattingConfig>): string {
    const cfg = { ...this.current, ...override } as CurrencyFormattingConfig;
    let num = (typeof value === 'string') ? Number(value) : value;
    if (isNaN(num)) return '';

  const precision = Math.max(0, cfg.precision);
  const fixed = num.toFixed(precision);
    const [intPartRaw, decPart] = fixed.split('.');

    // Add thousand separators
    const intPart = intPartRaw.replace(/\B(?=(\d{3})+(?!\d))/g, cfg.thousandSeparator);

    const decimalSection = precision > 0 ? cfg.decimalSeparator + decPart : '';
    const core = intPart + decimalSection;

    return cfg.symbolPosition === 'before' ? `${cfg.symbol}${core}` : `${core} ${cfg.symbol}`;
  }
}
